<script>
	import { onMount } from 'svelte';
	import Navbar from '../components/Navbar.svelte';
	import { fetchWorkspace, updateWorkspace, fetchMembers, inviteMember, removeMember, purchaseSeats } from '../lib/api.js';

	export let navigate;

	// ---- State ----

	let loading = true;

	let workspace = { name: '', alias: '', created_at: null, doc_count: 0, topic_count: 0, plan_tier: 'team', seat_count: 5 };
	let members = [];
	let saving = false;
	let inviteEmail = '';
	let inviteRole = 'member';
	let showInviteRow = false;
	let inviting = false;
	let showSeatsModal = false;
	let seatsConfirming = false;

	const SEAT_PACK = 5;
	const PRICE_PER_SEAT = 29;

	// ---- Data fetch ----

	onMount(async () => {
		try {
			const [wsData, memData] = await Promise.all([
				fetchWorkspace(),
				fetchMembers()
			]);
			workspace = wsData.workspace;
			members = memData.members;
		} catch (err) {
			// Fail silently — page shows empty state
		}
		loading = false;
	});

	// ---- Actions ----

	async function saveWorkspaceName() {
		if (saving || !workspace.name.trim()) return;
		saving = true;
		try {
			const { workspace: updated } = await updateWorkspace({ name: workspace.name.trim() });
			workspace = updated;
		} catch (err) {
			// TODO: show error toast
		}
		saving = false;
	}

	async function sendInvite() {
		if (inviting || !inviteEmail.trim()) return;
		inviting = true;
		try {
			const { member } = await inviteMember(inviteEmail.trim(), inviteRole);
			// Add to local list and reset form
			members = [...members, { ...member, joined_at: new Date().toISOString(), last_active_at: null }];
			inviteEmail = '';
			showInviteRow = false;
		} catch (err) {
			// TODO: show error toast (e.g. "already a member" or "seat limit")
		}
		inviting = false;
	}

	async function handleRemove(userId) {
		try {
			await removeMember(userId);
			members = members.filter(m => m.id !== userId);
		} catch (err) {
			// TODO: show error toast
		}
	}

	async function confirmSeats() {
		seatsConfirming = true;
		try {
			const result = await purchaseSeats(SEAT_PACK);
			workspace.seat_count = result.seat_count;
			workspace = workspace;
			showSeatsModal = false;
		} catch (err) {
			// TODO: show error toast
		}
		seatsConfirming = false;
	}

	// ---- Helpers ----

	function formatDate(dateStr) {
		if (!dateStr) return '--';
		const d = new Date(dateStr);
		return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
	}

	function daysAgo(dateStr) {
		if (!dateStr) return '--';
		const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
		if (days === 0) return 'today';
		if (days === 1) return 'yesterday';
		return `${days}d ago`;
	}
</script>

<Navbar {navigate} active="admin" />

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div class="admin-page">
	<div class="admin-header">
		<h1 class="admin-title">Settings</h1>
	</div>

	<div class="admin-body">
		<!-- ============================================================
		     WORKSPACE
		     ============================================================ -->
		<div class="section-card">
			<h2 class="section-title">Workspace</h2>
			<div class="name-row">
				<input class="field-input" type="text" bind:value={workspace.name} />
				<button class="btn-primary" on:click={saveWorkspaceName} disabled={saving}>
					{saving ? 'Saving...' : 'Save'}
				</button>
			</div>
			<div class="meta-row">
				{#if workspace.alias}
					<span class="meta-item">Alias: <strong>{workspace.alias}</strong></span>
					<span class="meta-dot">&middot;</span>
				{/if}
				<span class="meta-item">Created {formatDate(workspace.created_at)}</span>
				<span class="meta-dot">&middot;</span>
				<span class="meta-item">{workspace.doc_count} docs</span>
				<span class="meta-dot">&middot;</span>
				<span class="meta-item">{workspace.topic_count} topics</span>
			</div>
		</div>

		<!-- ============================================================
		     USERS
		     ============================================================ -->
		<div class="section-card">
			<div class="section-header-row">
				<h2 class="section-title">Team Members</h2>
				<div class="header-actions">
					<button class="btn-secondary" on:click={() => { showInviteRow = !showInviteRow; }}>
						{showInviteRow ? 'Cancel' : 'Invite'}
					</button>
					<button class="btn-secondary" on:click={() => { showSeatsModal = true; }}>+{SEAT_PACK} Seats</button>
				</div>
			</div>

			{#if showInviteRow}
				<div class="invite-row">
					<input class="field-input invite-input" type="email" placeholder="email@company.com" bind:value={inviteEmail} />
					<select class="field-select" bind:value={inviteRole}>
						<option value="member">Member</option>
						<option value="admin">Admin</option>
					</select>
					<button class="btn-primary" disabled={!inviteEmail || inviting} on:click={sendInvite}>
						{inviting ? 'Inviting...' : 'Send Invite'}
					</button>
				</div>
			{/if}

			<div class="members-table">
				<div class="members-header">
					<span class="col-name">Name</span>
					<span class="col-email">Email</span>
					<span class="col-role">Role</span>
					<span class="col-active">Last Active</span>
					<span class="col-actions"></span>
				</div>
				{#each members as member}
					<div class="member-row">
						<span class="col-name">{member.name}</span>
						<span class="col-email">{member.email}</span>
						<span class="col-role">
							<span class="role-badge" class:role-admin={member.role === 'admin'}>{member.role}</span>
						</span>
						<span class="col-active">{daysAgo(member.last_active_at)}</span>
						<span class="col-actions">
							{#if member.role !== 'admin'}
								<span class="action-link" on:click={() => handleRemove(member.id)}>Remove</span>
							{/if}
						</span>
					</div>
				{/each}
			</div>

			<div class="seats-note">
				{members.length} of {workspace.seat_count || 5} seats used
			</div>
		</div>

		<!-- ============================================================
		     PLAN
		     ============================================================ -->
		<div class="section-card">
			<h2 class="section-title">Plan</h2>
			<div class="plan-row">
				<div class="plan-info">
					<span class="plan-name">{(workspace.plan_tier || 'team').charAt(0).toUpperCase() + (workspace.plan_tier || 'team').slice(1)}</span>
					<span class="plan-price">${PRICE_PER_SEAT}/seat/mo</span>
				</div>
				<span class="plan-status">active</span>
			</div>
			<div class="plan-detail">
				{members.length} of {workspace.seat_count || 5} seats used
			</div>
		</div>
	</div>
</div>

{#if showSeatsModal}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<div class="modal-overlay" on:click={() => { if (!seatsConfirming) showSeatsModal = false; }}>
		<!-- svelte-ignore a11y-click-events-have-key-events -->
		<div class="modal" on:click|stopPropagation>
			<h3 class="modal-title">Add {SEAT_PACK} Seats</h3>
			<p class="modal-text">
				Your plan will increase from {workspace.seat_count || 5} to {(workspace.seat_count || 5) + SEAT_PACK} seats.
			</p>
			<div class="modal-price">
				<span class="price-amount">${SEAT_PACK * PRICE_PER_SEAT}</span>
				<span class="price-period">/mo added</span>
			</div>
			<p class="modal-sub">Prorated to your current billing cycle.</p>
			<div class="modal-actions">
				<button class="btn-secondary" on:click={() => { showSeatsModal = false; }} disabled={seatsConfirming}>Cancel</button>
				<button class="btn-primary" on:click={confirmSeats} disabled={seatsConfirming}>
					{seatsConfirming ? 'Processing...' : 'Confirm'}
				</button>
			</div>
		</div>
	</div>
{/if}

<style>
	.admin-page {
		min-height: 100vh;
		background: #F9FAFB;
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
		-webkit-font-smoothing: antialiased;
	}

	/* ================================================================
	   HEADER
	   ================================================================ */

	.admin-header {
		background: #FFFFFF;
		border-bottom: 1px solid #E5E7EB;
		padding: 28px 24px;
	}

	.admin-title {
		margin: 0 auto;
		max-width: 640px;
		font-size: 20px;
		font-weight: 700;
		color: #111827;
		letter-spacing: -0.3px;
	}

	/* ================================================================
	   BODY
	   ================================================================ */

	.admin-body {
		max-width: 640px;
		margin: 0 auto;
		padding: 24px 24px 80px;
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	/* ================================================================
	   SECTION CARDS
	   ================================================================ */

	.section-card {
		background: #FFFFFF;
		border: 1px solid #E5E7EB;
		border-radius: 10px;
		padding: 24px 28px;
	}

	.section-title {
		margin: 0 0 16px;
		font-size: 14px;
		font-weight: 600;
		color: #111827;
		letter-spacing: -0.2px;
	}

	.section-header-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;
	}
	.section-header-row .section-title { margin: 0; }

	.header-actions {
		display: flex;
		gap: 8px;
	}

	/* ================================================================
	   WORKSPACE
	   ================================================================ */

	.name-row {
		display: flex;
		gap: 10px;
		align-items: center;
	}

	.name-row .field-input { flex: 1; }

	.meta-row {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 6px;
		margin-top: 12px;
		font-size: 12px;
		color: #9CA3AF;
	}

	.meta-item strong {
		font-weight: 600;
		color: #6B7280;
	}

	.meta-dot { color: #D1D5DB; }

	/* ================================================================
	   FIELDS
	   ================================================================ */

	.field-input {
		padding: 8px 12px;
		font-size: 14px;
		color: #111827;
		border: 1px solid #D1D5DB;
		border-radius: 6px;
		background: #FFFFFF;
		box-sizing: border-box;
		transition: border-color 150ms ease-out;
	}
	.field-input:focus { outline: none; border-color: #6B7280; }

	.field-select {
		padding: 8px 12px;
		font-size: 13px;
		color: #111827;
		border: 1px solid #D1D5DB;
		border-radius: 6px;
		background: #FFFFFF;
	}

	/* ================================================================
	   BUTTONS
	   ================================================================ */

	.btn-primary {
		padding: 8px 20px;
		font-size: 13px;
		font-weight: 600;
		color: #FFFFFF;
		background: #111827;
		border: none;
		border-radius: 6px;
		cursor: pointer;
		transition: background 150ms ease-out;
		white-space: nowrap;
	}
	.btn-primary:hover { background: #1F2937; }
	.btn-primary:disabled { opacity: 0.4; cursor: default; }

	.btn-secondary {
		padding: 6px 16px;
		font-size: 12px;
		font-weight: 600;
		color: #374151;
		background: #FFFFFF;
		border: 1px solid #D1D5DB;
		border-radius: 6px;
		cursor: pointer;
		transition: all 150ms ease-out;
		white-space: nowrap;
	}
	.btn-secondary:hover { border-color: #9CA3AF; background: #F9FAFB; }

	/* ================================================================
	   MEMBERS TABLE
	   ================================================================ */

	.members-table {
		border: 1px solid #E5E7EB;
		border-radius: 8px;
		overflow: hidden;
	}

	.members-header {
		display: flex;
		align-items: center;
		padding: 8px 16px;
		background: #F9FAFB;
		border-bottom: 1px solid #E5E7EB;
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: #9CA3AF;
	}

	.member-row {
		display: flex;
		align-items: center;
		padding: 10px 16px;
		border-bottom: 1px solid #F3F4F6;
		font-size: 13px;
		color: #374151;
	}
	.member-row:last-child { border-bottom: none; }

	.col-name { flex: 1.2; font-weight: 500; color: #111827; }
	.col-email { flex: 1.5; color: #6B7280; font-size: 12px; }
	.col-role { width: 80px; flex-shrink: 0; }
	.col-active { width: 80px; flex-shrink: 0; font-size: 12px; color: #9CA3AF; }
	.col-actions { width: 60px; flex-shrink: 0; text-align: right; }

	.role-badge {
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		padding: 2px 8px;
		border-radius: 4px;
		background: #F3F4F6;
		color: #6B7280;
	}
	.role-admin { background: #111827; color: #F9FAFB; }

	.action-link {
		font-size: 11px;
		color: #9CA3AF;
		cursor: pointer;
		transition: color 150ms ease-out;
	}
	.action-link:hover { color: #DC2626; }

	.invite-row {
		display: flex;
		gap: 10px;
		margin-bottom: 16px;
		align-items: center;
	}
	.invite-input { flex: 1; }

	.seats-note {
		margin-top: 12px;
		font-size: 12px;
		color: #9CA3AF;
	}

	/* ================================================================
	   PLAN
	   ================================================================ */

	.plan-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.plan-info { display: flex; align-items: baseline; gap: 12px; }
	.plan-name { font-size: 24px; font-weight: 700; color: #111827; }
	.plan-price { font-size: 13px; color: #6B7280; }
	.plan-status {
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: #10B981;
	}

	.plan-detail {
		margin-top: 8px;
		font-size: 13px;
		color: #6B7280;
	}

	/* ================================================================
	   RESPONSIVE
	   ================================================================ */

	@media (max-width: 640px) {
		.admin-header { padding: 20px 16px; }
		.admin-body { padding: 16px 16px 60px; }
		.section-card { padding: 20px 20px; }
		.name-row { flex-direction: column; }
		.name-row .field-input { width: 100%; }
		.name-row .btn-primary { width: 100%; }
		.invite-row { flex-direction: column; }
		.invite-input { width: 100%; }
		.col-email { display: none; }
		.col-active { display: none; }
		.header-actions { flex-wrap: wrap; }
		.meta-row { flex-direction: column; gap: 2px; }
		.meta-dot { display: none; }
		.modal { width: 90%; }
		.modal-actions { flex-direction: column; }
		.modal-actions .btn-secondary,
		.modal-actions .btn-primary { width: 100%; }
	}

	/* ================================================================
	   MODAL
	   ================================================================ */

	.modal-overlay {
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.5);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 100;
		animation: fade-in 0.2s ease;
	}

	@keyframes fade-in {
		from { opacity: 0; }
		to { opacity: 1; }
	}

	.modal {
		background: #FFFFFF;
		border-radius: 12px;
		padding: 32px;
		width: 380px;
		max-width: 92vw;
		text-align: center;
		animation: slide-up 0.25s ease;
	}

	@keyframes slide-up {
		from { opacity: 0; transform: translateY(10px); }
		to { opacity: 1; transform: translateY(0); }
	}

	.modal-title {
		margin: 0 0 8px;
		font-size: 16px;
		font-weight: 700;
		color: #111827;
	}

	.modal-text {
		margin: 0 0 20px;
		font-size: 13px;
		color: #6B7280;
		line-height: 1.5;
	}

	.modal-price {
		display: flex;
		align-items: baseline;
		justify-content: center;
		gap: 4px;
		margin-bottom: 6px;
	}

	.price-amount {
		font-size: 32px;
		font-weight: 800;
		color: #111827;
		letter-spacing: -0.5px;
	}

	.price-period {
		font-size: 13px;
		color: #9CA3AF;
	}

	.modal-sub {
		margin: 0 0 24px;
		font-size: 12px;
		color: #9CA3AF;
	}

	.modal-actions {
		display: flex;
		gap: 10px;
		justify-content: center;
	}
</style>
